import { Check, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export interface GroupByOption {
  value: string
  label: string
}

interface GroupBySelectProps {
  /** Groupable columns. "None" is added by this component, never passed in. */
  options: GroupByOption[]
  /** Current grouping, or null for "None". */
  value: string | null
  onChange: (value: string | null) => void
}

/**
 * Group-by control for the Analysis tables.
 *
 * `outline` rather than `ghost`: this trigger shows a value and opens a menu of
 * choices, which is exactly the case where the design system keeps the border.
 */
export function GroupBySelect({ options, value, onChange }: GroupBySelectProps) {
  const active = options.find((option) => option.value === value) ?? null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5">
          {active ? `Group: ${active.label}` : 'Group by'}
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        <DropdownMenuItem onClick={() => onChange(null)} className="gap-2">
          <Check className={active ? 'h-3.5 w-3.5 opacity-0' : 'h-3.5 w-3.5'} aria-hidden="true" />
          None
        </DropdownMenuItem>
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => onChange(option.value)}
            className="gap-2"
          >
            <Check
              className={
                option.value === value ? 'h-3.5 w-3.5' : 'h-3.5 w-3.5 opacity-0'
              }
              aria-hidden="true"
            />
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
